import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

const getToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getMonthStart = (date: Date): Date => {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getMonthEnd = (date: Date): Date => {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(0, 0, 0, 0);
  return d;
};

interface MonthPickerProps {
  selectedMonth: Date;
  onMonthSelect: (m: Date) => void;
  onClose: () => void;
  minDate?: Date;
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const MonthPicker: React.FC<MonthPickerProps> = ({ selectedMonth, onMonthSelect, onClose, minDate }) => {
  const today = getToday();
  const [year, setYear] = useState(selectedMonth.getFullYear());

  const selYear = selectedMonth.getFullYear();
  const selMonth = selectedMonth.getMonth();

  const isDisabled = (y: number, m: number) => {
    const d = new Date(y, m, 1);
    if (d > today) return true;
    if (minDate) {
      const min = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      if (d < min) return true;
    }
    return false;
  };

  const handleClick = (m: number) => {
    if (isDisabled(year, m)) return;
    onMonthSelect(getMonthStart(new Date(year, m, 1)));
    onClose();
  };

  const canPrevYear = !minDate || year - 1 >= minDate.getFullYear();
  const canNextYear = year < today.getFullYear();

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-72">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => canPrevYear && setYear(year - 1)}
          disabled={!canPrevYear}
          className={`p-1 rounded transition ${!canPrevYear ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
          aria-label="Previous year"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="font-semibold text-gray-900">{year}</span>
        <button
          onClick={() => canNextYear && setYear(year + 1)}
          disabled={!canNextYear}
          className={`p-1 rounded transition ${!canNextYear ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
          aria-label="Next year"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {MONTHS_SHORT.map((m, i) => {
          const disabled = isDisabled(year, i);
          const selected = year === selYear && i === selMonth;
          return (
            <button
              key={m}
              onClick={() => handleClick(i)}
              disabled={disabled}
              className={`py-2 rounded-md text-sm font-inter transition-colors
                ${disabled ? "text-gray-300 cursor-not-allowed" : "cursor-pointer"}
                ${selected ? "bg-[#00A9A9] text-white font-semibold" : ""}
                ${!disabled && !selected ? "hover:bg-[#E0F5F5] text-[#1F1F1F]" : ""}
              `}
            >
              {m}
            </button>
          );
        })}
      </div>
    </div>
  );
};

interface MonthlyInsightsHeaderProps {
  onMonthChange?: (monthStart: Date, monthEnd: Date) => void;
  minDate?: Date;
}

const MonthlyInsightsHeader: React.FC<MonthlyInsightsHeaderProps> = ({
  onMonthChange,
  minDate,
}) => {
  const [monthStart, setMonthStart] = useState<Date>(() => getMonthStart(getToday()));
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleMonthSelect = (m: Date) => {
    setMonthStart(m);
    onMonthChange?.(m, getMonthEnd(m));
  };

  const shiftMonth = (delta: number) => {
    const next = new Date(monthStart.getFullYear(), monthStart.getMonth() + delta, 1);
    if (delta > 0 && next > getMonthStart(getToday())) return;
    handleMonthSelect(getMonthStart(next));
  };

  const canGoNext = getMonthStart(monthStart) < getMonthStart(getToday());
  const canGoPrev =
    !minDate ||
    monthStart > new Date(minDate.getFullYear(), minDate.getMonth(), 1);

  const formatMonth = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", year: "numeric" });

  return (
    <div ref={ref} className="relative flex items-center gap-1">
      <button
        onClick={() => canGoPrev && shiftMonth(-1)}
        disabled={!canGoPrev}
        className={`p-1 rounded transition ${!canGoPrev ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
      >
        <ChevronLeft className="w-4 h-4 text-gray-500" />
      </button>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#E5E7EB] bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="text-[13px] font-medium text-[#1F1F1F] font-inter whitespace-nowrap">
          {formatMonth(monthStart)}
        </span>
        <Calendar className="w-4 h-4 text-[#00A9A9]" />
      </button>
      <button
        onClick={() => canGoNext && shiftMonth(1)}
        disabled={!canGoNext}
        className={`p-1 rounded transition ${!canGoNext ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
      >
        <ChevronRight className="w-4 h-4 text-gray-500" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 z-50">
          <MonthPicker
            selectedMonth={monthStart}
            onMonthSelect={handleMonthSelect}
            onClose={() => setOpen(false)}
            minDate={minDate}
          />
        </div>
      )}
    </div>
  );
};

export default MonthlyInsightsHeader;
export { MonthPicker };
